/*global _ */

import CartMixins from '../../components/_mixins/location-checkout'
import ScrollMixins from '../../components/_mixins/scroller'
import PersonalDetailsMixins from '../../components/_mixins/personal-details'
import BookingMixin from './booking'

import {createNamespacedHelpers} from 'vuex'
import {default as store, absoluteNamespace} from '../../vuexstore/modules/event-reservation'
const {mapGetters, mapActions} = createNamespacedHelpers(absoluteNamespace)

export const mixins = [CartMixins, ScrollMixins, PersonalDetailsMixins, BookingMixin]

import ProviderService from '../../services/event-reservation'
import {parseDateTime, getNow} from '../../lib/dates'
import {alert, getQS} from '../../lib/utils'
import config from '../../lib/config'

const {
  TIMEZONE,
  NO_MIN_DATE = false
} = config

const qs = getQS() || {}
const noHold = !!qs.nohold
const timerTimeout = qs.timeout

/**
 *
 * @param options{storePath: '', get: '', set: '', prefix: '', suffix: ''}
 * @returns {*}
 */
export const mapState = (options) => {
  const {storePath, prefix = '', suffix = '', depth = null} = options
  const states =  _.reduce(storePath, (fns, item, key) => {
    const fnKey = prefix + key + suffix
    if (depth) {
      key = depth + '.' + key
    }
    fns[fnKey] = {
      get() {
        return this.input(key)
      },
      set(value) {
        this.setInput({[key]: value})
      }
    }
    return fns
  }, {})
  return states
}

export default {
  mixins,
  data() {
    return {
      timeZone: TIMEZONE,
      noMinDate: NO_MIN_DATE
    }
  },
  computed: {
    reservationProduct() {
      return this.currentProduct || {}
    },
    availableDurations() {
      const durations = _.values(this.reservationProduct.durations || [])

      durations.sort((a, b) => {
        return _.toFinite(a.value) - _.toFinite(b.value)
      })

      return _.keyBy(durations, 'value')
    },
    selectedDurationDetails() {
      return (this.availableDurations[this.duration] || {})
    },
    minParticipants() {
      return _.toFinite(this.reservationProduct.min_participants)
    },
    maxParticipants() {
      return _.toFinite(this.reservationProduct.max_participants)
    },
    moreThanMaxParticipants() {
      return this.participants > this.maxParticipants
    },
    isGroupEventDetected() {
      return this.moreThanMaxParticipants
    },
    maxParticipantsPerTrack() {
      return _.toFinite(this.reservationProduct.max_participants_per_track) || this.participants
    },
    numTracks() {
      return _.toFinite(Math.ceil(this.participants / this.maxParticipantsPerTrack))
    },
    isMultiTrack() {
      return this.numTracks > 1
    },
    maxDate() {
      //hours_in_advance ?
      const daysInFuture = _.toFinite(this.reservationProduct.days_in_future)
      if (daysInFuture <= 0) {
        return null
      }
      const now = getNow(TIMEZONE).startOf('day')
      const endDate = now.plus({ days: daysInFuture})
      return endDate.toISODate()
    },
    minDate() {
      const now = getNow(TIMEZONE).startOf('day')
      return NO_MIN_DATE ? null : now.toISODate()
    },
    disableWeekDays() {
      return this.$$s('disableWeekDays', null)
    },
    disableDates() {
      return this.$$s('disableDates', null)
    },
    availabilityBuffer() {
      return _.toFinite(this.reservationProduct.availability_buffer) || 0 //minutes
    },
    allOpenHours() {
      return this.reservationProduct.open_hours || []
    },
    timeoutDuration() {
      return _.toFinite(timerTimeout || this.reservationProduct.hold_timeout)
    },
    // order of tracks used when one track is required for booking
    tracksForSingleReservation() {
      const location = this.currentLocationId || 'global'
      return _.get(this.reservationProduct, `tracks.${location}`, [])
    },
    // order of tracks used when multiple tracks are required for booking
    tracksForMultiReservation() {
      const location = this.currentLocationId || 'global'
      return _.get(this.reservationProduct, `tracks_multi.${location}`, [])
    },
    allAvailableTracks() {
      // with different tracks for single and multi-track reservations
      return _.uniq(this.tracksForSingleReservation, ...this.tracksForMultiReservation)
    },

    availableTracks() {
      const numTracks = this.numTracks
      const singleTracks = this.tracksForSingleReservation;
      if (!this.isMultiTrack) {
        return singleTracks
      }

      const multi = this.tracksForMultiReservation;
      let filtered = multi.filter(i => i.length == numTracks);
      if (!filtered.length) {
        filtered = this.trackCombinations(singleTracks, numTracks)
        // debugLog({availableTracks: filtered})
      }
      return filtered
    },
    timeslotDuration() {
      return _.toFinite(this.reservationProduct.timeslot_duration)
    },
    productDuration() {
      return _.toFinite(this.reservationProduct.product_duration)
    },
    arrivalThresholds() {
      return _.mapValues(
        _.mapKeys(
          this.reservationProduct.arrival_threshold || [],
          item => (((item || {}).day || '') + '').toLowerCase()
        ),
        'minutes'
      )
    },
    confirmationStepTitle() {
      return _.get(this.reservationProduct, 'custom.confirm_step_title')
    },
    confirmationStepMessage() {
      return _.get(this.reservationProduct, 'custom.confirm_step_message')
    },
    ...mapGetters(Object.keys(store.getters)),
    ...mapState({storePath: store.state.form}),
    ...mapState({storePath: store.state.form.contact, depth: 'contact'}),
  },
  watch: {
    currentLocation: {
      handler(value) {
        this.setProductLocation(value)
      },
      immediate: true
    },
    currentLocationId: {
      handler(value) {
        this.setProductLocationId(value)
      },
      immediate: true
    },
  },
  methods: {
    // consecutive (wraparound) k-length windows of the given tracks
    consecutiveTracks(tracks, k) {
      const len = tracks.length
      const result = []
      if (k <= 0 || k > len) {
        return result
      }
      for (let i = 0; i < len; i++) {
        const item = []
        for (let j = 0; j < k; j++) {
          item.push(tracks[(i + j) % len])
        }
        result.push(item)
      }
      return result
    },
    // consecutive windows first, then all remaining k-combinations (deduped by set membership)
    trackCombinations(tracks, k) {
      const keyOf = set => set.map(t => (t && t.id != null) ? t.id : t).slice().sort().join('|')
      const result = this.consecutiveTracks(tracks, k)
      const seen = new Set(result.map(keyOf))
      const combine = (start, chosen) => {
        if (chosen.length === k) {
          const key = keyOf(chosen)
          if (!seen.has(key)) {
            seen.add(key)
            result.push(chosen.slice())
          }
          return
        }
        for (let i = start; i < tracks.length; i++) {
          chosen.push(tracks[i])
          combine(i + 1, chosen)
          chosen.pop()
        }
      }
      if (k > 0 && k <= tracks.length) {
        combine(0, [])
      }
      return result
    },
    async loadTimeslots(date, selectedSlots, parser) {
      date = date || this.selectedDate
      if (!date) {
        return
      }
      parser = parser || ((...args) => this.createTimeslots(...args))
      const product_id = this.reservationProduct.id
      const participants = this.participants
      const duration = this.duration
      const ignore_reservation_hold_id = _.filter(
        _.values(
          _.mapValues(selectedSlots || {}, v => _.get(v, 'hold.id'))
        )
      )
      await this.retrieveTimeslots({parser, payload: {date, product_id, participants, duration, ignore_reservation_hold_id}})
    },
    async askAndRemoveSelectedTimeSlot(reason, callback, denied, callbefore) {
      if (!_.isEmpty(this.selectedSlots)) {
        const options = {
          message: `Your selected time options will be removed due to ${reason}.`
        }
        options.confirmed = async () => {
          if (callbefore) {
            await callbefore()
          }
          this.cleanTimeslots()
          if (callback) {
            await callback()
          }
        }
        if (denied) {
          options.denied = denied
        }

        this.confirm(options)

      } else {
        if (callback) {
          callback()
        }
      }
    },
    isAnyProcessing() {
      return _.some(this.retrievedTimeslots, slot => !!slot.processing)
    },
    startHoldAnimation(time) {
      this.updateSelectedAndRetrievedTimeslot({time, processing: true})
    },
    endHoldAnimation(time) {
      this.updateSelectedAndRetrievedTimeslot({time, processing: false})
    },

    async holdSlot(slot, payload) {
      const {time} = slot
      this.startHoldAnimation(time)

      let newHold
      try {
        if (!noHold) {
          newHold = await ProviderService.hold(payload)
        }
      } catch (e) {
        const defaultMessage = 'Unable to set temporary hold on your selected slot. Please try again!'
        const message = ProviderService.getFirstErrorMessage(e, defaultMessage)
        alert(message, {classes: 'text-danger'})
      } finally {
        this.endHoldAnimation(time)
      }
      if (newHold) {
        const hold = _.merge({}, payload, newHold || {})
        this.updateSelectedAndRetrievedTimeslot({time, hold})
        return hold
      }
      return null
    },
    async unholdSlot({time}, noAnimation) {
      const slot = this.retrievedTimeslots[time] || {}
      const {hold = {}} = slot || {}
      const oldHold = _.cloneDeep(hold)
      this.deselectTimeslot({time})

      if (oldHold.id && !noHold) {
        if (!noAnimation) {
          this.startHoldAnimation(time)
        }
        try {
          await ProviderService.unhold(oldHold)
        } catch (e) {
          //
        }
        if (!noAnimation) {
          this.endHoldAnimation(time)
        }
      }
    },
    getEarliestSlotTime(item, slots) {
      slots = slots || _.values(_.get(item, 'payload.options.form.selectedSlots', {}))
      if (!_.isEmpty(slots)) {
        const slot = _.minBy(slots, o => {
          return parseDateTime(o.datetime).valueOf()
        })
        if (slot) {
          return parseDateTime(slot.datetime).toSeconds()
        }
      }
      return null
    },
    ...mapActions(Object.keys(store.actions))
  },
}
